import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useSession } from "@/frontend/auth-client";
import { orpc, orpcClient } from "@/frontend/orpc-client";
import { WORKSPACE_HOME_PATH } from "@/frontend/types/org";
import { PersonaWorkspaceForm } from "./PersonaWorkspaceForm";
import { OnboardingPageLayout } from "./OnboardingPageLayout";
import {
  clearInviteResumeId,
  getRememberedInviteResumeId,
} from "@/frontend/utils/invite-flow";
import { groupEntryWorkspace, groupWorkspaces } from "@/frontend/utils/dashboard/workspaces";

type Props = {
  children: ReactNode;
};

type SetActiveOrganizationFallbackProps = {
  firstOrgId: string | undefined;
  children?: ReactNode;
};

/**
 * Recovers a session whose active workspace was lost by activating the member's
 * first workspace. Renders `children` when there is none to pick. The repair
 * runs as a query rather than an effect, so React Query owns the once-per-id
 * guard, and the refetch it triggers replaces what used to be a full reload.
 */
function SetActiveOrganizationFallback({
  firstOrgId,
  children,
}: SetActiveOrganizationFallbackProps) {
  const queryClient = useQueryClient();

  useQuery({
    queryKey: ["repair-active-organization", firstOrgId],
    queryFn: async () => {
      await orpcClient.org.setActiveOrganization({ organizationId: firstOrgId! });
      await queryClient.invalidateQueries();
      return firstOrgId;
    },
    enabled: !!firstOrgId,
    staleTime: Infinity,
    retry: false,
  });

  if (!firstOrgId && children) return <>{children}</>;

  return (
    <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
      Setting active workspace...
    </div>
  );
}

/**
 * Makes sure a signed-in org member has a workspace before the dashboard
 * renders. Signed-out visitors, patients, the invite-accept page and the public
 * delete-patient-data page all pass straight through. A member with no workspace
 * resumes a remembered invite or runs the persona and workspace setup steps.
 */
export function OrganizationGate({ children }: Props) {
  const navigate = useNavigate();
  const { data: session, isPending: sessionPending } = useSession();
  const isInviteAcceptPage = window.location.pathname === "/invite/accept";
  // `/delete-your-data` is a public patient page with its own portal sign-in.
  // A signed-in org member landing on it must reach the page, not a workspace prompt.
  const isDeletePatientDataPage = window.location.pathname === "/delete-your-data";
  const actorContextQuery = useQuery({
    ...orpc.org.getActorContext.queryOptions({ input: {} }),
    enabled: !!session,
  });

  const organizationsQuery = useQuery({
    ...orpc.org.listOrganizations.queryOptions({ input: {} }),
    enabled: !!session,
  });
  const activeOrgQuery = useQuery({
    ...orpc.org.getFullOrganization.queryOptions({ input: {} }),
    enabled: !!session,
  });
  const rememberedInviteId = getRememberedInviteResumeId();

  if (sessionPending) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading
      </div>
    );
  }

  if (!session) {
    return <>{children}</>;
  }

  if (actorContextQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading
      </div>
    );
  }

  if (isInviteAcceptPage || isDeletePatientDataPage) {
    return <>{children}</>;
  }

  if (actorContextQuery.data?.actorType === "patient") {
    return <>{children}</>;
  }

  if (organizationsQuery.isLoading || activeOrgQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading workspace...
      </div>
    );
  }

  const orgs = organizationsQuery.data ?? [];
  const activeOrg = activeOrgQuery.data;

  // User has no orgs and would see the persona step. If they have a pending
  // invite that they were already working through, resume that exact flow.
  if (!orgs.length && !isInviteAcceptPage) {
    if (rememberedInviteId) {
      navigate({ to: "/invite/accept", search: { id: rememberedInviteId } });
      return (
        <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
          Redirecting to invitation…
        </div>
      );
    }
    clearInviteResumeId();
    // First workspace: ask whose records before creating anything.
    return (
      <OnboardingPageLayout>
        <PersonaWorkspaceForm onCompleted={() => window.location.assign(WORKSPACE_HOME_PATH)} />
      </OnboardingPageLayout>
    );
  }

  if (!activeOrg && orgs.length && !isInviteAcceptPage) {
    clearInviteResumeId();
    // the first workspace group opens on its sandbox
    const firstGroup = groupWorkspaces(orgs)[0];
    return (
      <SetActiveOrganizationFallback firstOrgId={firstGroup ? groupEntryWorkspace(firstGroup).id : undefined} />
    );
  }

  return <>{children}</>;
}
